import React, { useEffect, useState } from "react";
import {
  Grid,
  TextField,
  Button,
  Paper,
  Typography,
  Alert,
  Box,
  Chip,
  Stack,
  Divider,
  useTheme,
  alpha,
  Autocomplete,
  ToggleButton,
  ToggleButtonGroup,
  MenuItem,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import BusinessIcon from "@mui/icons-material/Business";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import CategoryIcon from "@mui/icons-material/Category";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import SucursalAutocomplete from "../../resumenSucursal/components/SucursalAutocomplete";
import { obtenerTiposCliente } from "../services/listadoClientes.service";

const formatearPeriodo = (periodo) => {
  if (!periodo) return "";
  const [anio, mes] = periodo.split("-");
  return `${mes}/${anio}`;
};

const OPCIONES_ORDEN = [
  { value: "nombre", label: "Nombre" },
  { value: "sisven_id", label: "N° Sisven" },
  { value: "categoria", label: "Categoría / Tipo cliente" },
  { value: "kilos", label: "Kilos vendidos" },
  { value: "precio_kg", label: "Precio x kg (última compra)" },
  { value: "chofer", label: "Chofer (última compra)" },
];

const FiltrosListado = ({ onExportar, loading }) => {
  const theme = useTheme();
  const [sucursal, setSucursal] = useState(null);
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");
  const [tiposCliente, setTiposCliente] = useState([]);
  const [tiposClienteSeleccionados, setTiposClienteSeleccionados] = useState([]);
  const [ordenPor, setOrdenPor] = useState("nombre");
  const [ordenDir, setOrdenDir] = useState("asc");
  const [errorValidacion, setErrorValidacion] = useState(null);

  useEffect(() => {
    obtenerTiposCliente()
      .then((data) => setTiposCliente(Array.isArray(data) ? data : []))
      .catch(() => setTiposCliente([]));
  }, []);

  const isFormValid = sucursal && desde && hasta;

  const handleExportar = () => {
    if (!isFormValid) {
      setErrorValidacion("Seleccioná sucursal, mes desde y mes hasta.");
      return;
    }
    if (desde > hasta) {
      setErrorValidacion("El mes 'desde' no puede ser posterior al mes 'hasta'.");
      return;
    }
    setErrorValidacion(null);
    onExportar({
      sucursal,
      desde,
      hasta,
      tiposCliente: tiposClienteSeleccionados,
      ordenPor,
      ordenDir,
    });
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        mb: 2,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 2,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <FilterAltIcon sx={{ mr: 1, fontSize: 20, color: theme.palette.primary.main }} />
          <Typography variantMp="subtitle1" sx={{ fontWeight: 600 }}>
            Filtros del reporte
          </Typography>
        </Box>

        {(sucursal || desde || hasta) && (
          <Stack direction="row" spacing={0.75} flexWrap="wrap">
            {sucursal && (
              <Chip
                size="small"
                icon={<BusinessIcon sx={{ fontSize: 15 }} />}
                label={sucursal.nombre}
                color="primary"
                variant="filled"
                sx={{ height: 24, "& .MuiChip-label": { fontSize: "0.7rem", px: 0.75 } }}
              />
            )}
            {desde && hasta && (
              <Chip
                size="small"
                icon={<CalendarMonthIcon sx={{ fontSize: 15 }} />}
                label={`${formatearPeriodo(desde)} - ${formatearPeriodo(hasta)}`}
                color="primary"
                variant="outlined"
                sx={{ height: 24, "& .MuiChip-label": { fontSize: "0.7rem", px: 0.75 } }}
              />
            )}
            {tiposClienteSeleccionados.length > 0 && (
              <Chip
                size="small"
                icon={<CategoryIcon sx={{ fontSize: 15 }} />}
                label={
                  tiposClienteSeleccionados.length === 1
                    ? tiposClienteSeleccionados[0].descripcion
                    : `${tiposClienteSeleccionados.length} tipos de cliente`
                }
                color="primary"
                variant="outlined"
                sx={{ height: 24, "& .MuiChip-label": { fontSize: "0.7rem", px: 0.75 } }}
              />
            )}
          </Stack>
        )}
      </Box>

      <Divider sx={{ mb: 2 }} />

      <Grid container spacing={2} alignItems="flex-end">
        <Grid item xs={12} md={4}>
          <Typography
            variant="caption"
            color="textSecondary"
            sx={{ mb: 0.5, display: "block", fontWeight: 600, letterSpacing: 0.3 }}
          >
            SUCURSAL
          </Typography>
          <SucursalAutocomplete value={sucursal} onChange={setSucursal} disabled={loading} size="small" />
        </Grid>

        <Grid item xs={6} md={3}>
          <Typography
            variant="caption"
            color="textSecondary"
            sx={{ mb: 0.5, display: "block", fontWeight: 600, letterSpacing: 0.3 }}
          >
            DESDE (MES)
          </Typography>
          <TextField
            fullWidth
            size="small"
            type="month"
            value={desde}
            onChange={(e) => setDesde(e.target.value)}
            InputLabelProps={{ shrink: true }}
            disabled={loading}
            sx={{ bgcolor: "background.paper" }}
          />
        </Grid>

        <Grid item xs={6} md={3}>
          <Typography
            variant="caption"
            color="textSecondary"
            sx={{ mb: 0.5, display: "block", fontWeight: 600, letterSpacing: 0.3 }}
          >
            HASTA (MES)
          </Typography>
          <TextField
            fullWidth
            size="small"
            type="month"
            value={hasta}
            onChange={(e) => setHasta(e.target.value)}
            InputLabelProps={{ shrink: true }}
            disabled={loading}
            sx={{ bgcolor: "background.paper" }}
          />
        </Grid>

        <Grid item xs={12} md={2}>
          <Button
            fullWidth
            variant="contained"
            onClick={handleExportar}
            disabled={loading || !isFormValid}
            startIcon={<DownloadIcon />}
            sx={{
              height: 40,
              textTransform: "none",
              fontWeight: 600,
              boxShadow: isFormValid ? theme.shadows[2] : "none",
              "&:hover": { boxShadow: theme.shadows[4] },
            }}
          >
            {loading ? "Generando..." : "Exportar"}
          </Button>
        </Grid>
      </Grid>

      <Grid container spacing={2} alignItems="flex-end" sx={{ mt: 0.5 }}>
        <Grid item xs={12} md={5}>
          <Typography
            variant="caption"
            color="textSecondary"
            sx={{ mb: 0.5, display: "block", fontWeight: 600, letterSpacing: 0.3 }}
          >
            TIPO DE CLIENTE (CATEGORÍA)
          </Typography>
          <Autocomplete
            multiple
            size="small"
            disabled={loading}
            options={tiposCliente}
            value={tiposClienteSeleccionados}
            onChange={(_, value) => setTiposClienteSeleccionados(value)}
            getOptionLabel={(option) => option.descripcion || ""}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => (
              <TextField {...params} placeholder="Todos" sx={{ bgcolor: "background.paper" }} />
            )}
          />
        </Grid>

        <Grid item xs={8} md={4}>
          <Typography
            variant="caption"
            color="textSecondary"
            sx={{ mb: 0.5, display: "block", fontWeight: 600, letterSpacing: 0.3 }}
          >
            ORDENAR POR
          </Typography>
          <TextField
            select
            fullWidth
            size="small"
            value={ordenPor}
            onChange={(e) => setOrdenPor(e.target.value)}
            disabled={loading}
            sx={{ bgcolor: "background.paper" }}
          >
            {OPCIONES_ORDEN.map((opcion) => (
              <MenuItem key={opcion.value} value={opcion.value}>
                {opcion.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item xs={4} md={3}>
          <Typography
            variant="caption"
            color="textSecondary"
            sx={{ mb: 0.5, display: "block", fontWeight: 600, letterSpacing: 0.3 }}
          >
            DIRECCIÓN
          </Typography>
          <ToggleButtonGroup
            exclusive
            fullWidth
            size="small"
            value={ordenDir}
            onChange={(_, value) => value && setOrdenDir(value)}
            disabled={loading}
            sx={{ height: 40 }}
          >
            <ToggleButton
              value="asc"
              sx={{ textTransform: "none", px: 1, minWidth: 0 }}
              title="Ascendente"
            >
              <ArrowUpwardIcon sx={{ fontSize: 18 }} />
            </ToggleButton>
            <ToggleButton
              value="desc"
              sx={{ textTransform: "none", px: 1, minWidth: 0 }}
              title="Descendente"
            >
              <ArrowDownwardIcon sx={{ fontSize: 18 }} />
            </ToggleButton>
          </ToggleButtonGroup>
        </Grid>
      </Grid>

      {errorValidacion ? (
        <Alert severity="warning" sx={{ mt: 2 }} onClose={() => setErrorValidacion(null)}>
          {errorValidacion}
        </Alert>
      ) : (
        !isFormValid && (
          <Alert
            severity="info"
            variant="outlined"
            sx={{ mt: 2, py: 0, "& .MuiAlert-message": { fontSize: "0.75rem" } }}
          >
            Seleccioná una sucursal y un rango de meses para habilitar la exportación.
          </Alert>
        )
      )}
    </Paper>
  );
};

export default FiltrosListado;
